import {
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faClone,
  faFloppyDisk,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "@ventumsoftware/react-toolkit";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useFetchedResource } from "../../api/APIContext";
import { useApiCall, ValidationError } from "../../api/api";
import { InspectionFinding } from "../../api/dto/InspectionFinding";
import type { Question, QuestionAnswerValue } from "../../api/dto/Question";
import { localizeText } from "../../util/localizeText";
import { renderQuestionText } from "../../util/richText";
import { useUnsavedChangesPrompt } from "../../util/useUnsavedChangesPrompt";
import { Badge, BadgeStyle } from "../data/Badge";
import { Button, IconButton } from "../form/Button";
import { Loading } from "../Loading";
import { useHasOpenModal } from "../layout/ModalProvider";
import { useRefreshEffect } from "../RefreshController";
import { QuestionAnswerCell } from "./questionCells/QuestionAnswerCell";
import { type AvailableSeverities, type FindingChange, QuestionFindingCell } from "./questionCells/QuestionFindingCell";
import type { EnrichedInspection } from "./types";

interface QuestionRow {
  id: string;
  question: Question;
  answer: QuestionAnswerValue;
  findings: InspectionFinding[];
}

const questionRowElementId = (questionId: string) => `inspection-question-${questionId}`;

const AUTOSAVE_DELAY_MS = 1500;

export const InspectionQuestionsList: FC<{
  inspection: EnrichedInspection;
}> = (props) => {
  const {
    getInspectionAnswers,
    updateInspectionAnswers,
    listFindings,
    listAvailableInspectionSeverities,
    updateFinding,
    deleteFinding,
  } = useApiCall();

  const answersResource = useFetchedResource((inspectionId: string) => getInspectionAnswers(inspectionId));
  const findingsResource = useFetchedResource((inspectionId: string) => listFindings(inspectionId));
  const availableSeveritiesResource = useFetchedResource((inspectionId: string) =>
    listAvailableInspectionSeverities(inspectionId),
  );

  useRefreshEffect(() => {
    answersResource.reload(props.inspection.id);
    findingsResource.reload(props.inspection.id);
    availableSeveritiesResource.reload(props.inspection.id);
  }, [props.inspection.id]);

  const [answers, setAnswers] = useState<Record<string, QuestionAnswerValue>>({});
  // Tracks the last loaded/saved snapshot so "dirty" can be derived by reference comparison.
  const savedAnswersRef = useRef<Record<string, QuestionAnswerValue> | null>(null);
  const answersDirty = savedAnswersRef.current !== null && answers !== savedAnswersRef.current;
  const [pendingFindingChanges, setPendingFindingChanges] = useState<Record<string, FindingChange>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Set after a failed save so autosave doesn't keep retrying until the user edits again (or saves manually).
  const autosaveSuspendedRef = useRef(false);
  const hasOpenModal = useHasOpenModal();
  const dirty = answersDirty || Object.keys(pendingFindingChanges).length > 0;
  useUnsavedChangesPrompt(dirty);

  useEffect(() => {
    if (answersResource.resource) {
      setAnswers(answersResource.resource);
      savedAnswersRef.current = answersResource.resource;
    }
  }, [answersResource.resource]);

  const setAnswer = (questionId: string, value: QuestionAnswerValue) => {
    autosaveSuspendedRef.current = false;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const stageFindingChange = (change: FindingChange) => {
    autosaveSuspendedRef.current = false;
    // Keyed by finding (not question): a question can hold several findings (duplicates), and a
    // staged delete of one must not be overwritten by e.g. adding another on the same question.
    setPendingFindingChanges((prev) => ({ ...prev, [change.id]: change }));
  };

  const findings = findingsResource.resource ?? [];
  // All findings per question, with staged changes applied. Normally at most one per question.
  const findingsByQuestion = useMemo(() => {
    const byId = new Map(findings.map((finding) => [finding.id, finding]));
    for (const change of Object.values(pendingFindingChanges)) {
      if (change.type === "delete") {
        byId.delete(change.id);
      } else {
        const existing = byId.get(change.id);
        byId.set(
          change.id,
          new InspectionFinding(
            change.id,
            props.inspection.id,
            change.questionId,
            change.description,
            change.severity,
            change.comments,
            existing?.canStillBeFixed ?? true,
            existing?.question ?? null,
          ),
        );
      }
    }
    const map = new Map<string, InspectionFinding[]>();
    for (const finding of byId.values()) {
      map.set(finding.inspectionQuestionId, [...(map.get(finding.inspectionQuestionId) ?? []), finding]);
    }
    return map;
  }, [findings, pendingFindingChanges, props.inspection.id]);

  const questions = useMemo(
    () =>
      (props.inspection.questionSet?.questions ?? [])
        .filter((question) => !question.hide)
        .sort((a, b) => a.order - b.order || a.number.padStart(3, "0").localeCompare(b.number.padStart(3, "0"))),
    [props.inspection.questionSet?.questions],
  );

  const rows: QuestionRow[] = useMemo(
    () =>
      questions.map((question) => ({
        id: question.id,
        question,
        answer: answers[question.id],
        findings: findingsByQuestion.get(question.id) ?? [],
      })),
    [questions, answers, findingsByQuestion],
  );

  const rowsWithDuplicateFindings = useMemo(() => rows.filter((row) => row.findings.length > 1), [rows]);

  // `non_question` rows are section-label markers in the data (e.g. "Veiligheid", "Gedrag") -
  // each one starts a new card, titled with its own text, rather than being shown as a row.
  const groupedRows = useMemo(() => {
    const groups: { title: string; description: string; rows: QuestionRow[] }[] = [];
    for (const row of rows) {
      if (row.question.type === "non_question") {
        groups.push({
          title: localizeText(row.question.header) || row.question.displayCategory || "Vragen",
          description: localizeText(row.question.description),
          rows: [],
        });
        continue;
      }
      if (groups.length === 0) {
        groups.push({ title: row.question.displayCategory ?? "Vragen", description: "", rows: [] });
      }
      groups[groups.length - 1].rows.push(row);
    }
    return groups;
  }, [rows]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    autosaveSuspendedRef.current = false;
    // Edits can come in while the requests are in flight, so only what was snapshotted here is
    // marked as saved afterwards - anything newer stays dirty.
    const answersSnapshot = answers;
    const persistedChanges: FindingChange[] = [];
    let answersPersisted = false;
    try {
      // Findings go first: saving answers makes the backend generate missing findings for them,
      // so a finding added by hand in the same batch has to exist by then - otherwise the backend
      // generates its own and the question ends up with two. A failing finding must not block
      // saving the answers though, so its error is only raised once the answers are saved.
      let findingError: unknown = null;
      for (const change of Object.values(pendingFindingChanges)) {
        try {
          if (change.type === "delete") {
            await deleteFinding(props.inspection.id, change.id);
          } else {
            await updateFinding(
              props.inspection.id,
              change.id,
              change.questionId,
              change.severity,
              change.description,
              change.comments,
            );
          }
          persistedChanges.push(change);
        } catch (e) {
          findingError ??= e;
        }
      }
      if (answersSnapshot !== savedAnswersRef.current) {
        await updateInspectionAnswers(props.inspection.id, answersSnapshot);
        savedAnswersRef.current = answersSnapshot;
        answersPersisted = true;
      }
      if (findingError !== null) {
        throw findingError;
      }
    } catch (e) {
      autosaveSuspendedRef.current = true;
      setSaveError(
        e instanceof ValidationError
          ? Object.values(e.errors).flat().join(", ") || e.message
          : "Opslaan is mislukt, probeer het opnieuw.",
      );
    } finally {
      if (persistedChanges.length > 0 || answersPersisted) {
        // Reloaded silently (no spinner) so the list keeps its scroll position and focus, and
        // before dropping the staged changes so the persisted values never flash back to stale ones.
        // Also after an answers-only save, so findings the backend generated for a new answer show
        // up (instead of the user adding a second one by hand). That generation runs on a queue,
        // so it can still land after this reload - the duplicate warning below covers that case.
        await Promise.all([
          findingsResource.reload(props.inspection.id, true),
          availableSeveritiesResource.reload(props.inspection.id, true),
        ]);
        setPendingFindingChanges((prev) => {
          const remaining = { ...prev };
          for (const change of persistedChanges) {
            if (remaining[change.id] === change) {
              delete remaining[change.id];
            }
          }
          return remaining;
        });
      }
      setSaving(false);
    }
  };

  // Autosave shortly after the last edit. Paused while a modal is open (e.g. the finding editor
  // that pops up when a changed answer invalidates the finding's classification).
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: `answers` and `pendingFindingChanges` restart the debounce on every edit
  useEffect(() => {
    if (!dirty || saving || hasOpenModal || autosaveSuspendedRef.current) {
      return;
    }
    const timeout = setTimeout(() => saveRef.current(), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [answers, pendingFindingChanges, dirty, saving, hasOpenModal]);

  return (
    <Loading loading={answersResource.loading || findingsResource.loading}>
      <div
        className={
          "sticky top-2 z-10 bg-white rounded-sm border border-gray-200 px-6 py-3 mb-4 flex items-center justify-between"
        }
      >
        <Badge style={saving ? BadgeStyle.info : dirty ? BadgeStyle.warning : BadgeStyle.success}>
          <FontAwesomeIcon
            icon={saving ? faCircleNotch : dirty ? faCircleExclamation : faCheckCircle}
            spin={saving}
            className={"mr-1"}
          />
          {saving ? "Opslaan..." : dirty ? "Niet-opgeslagen wijzigingen" : "Alle antwoorden zijn opgeslagen"}
        </Badge>
        <div className={"flex items-center space-x-3"}>
          {saveError && <span className={"text-sm text-red-700"}>{saveError}</span>}
          <IconButton
            icon={faRefresh}
            size={"md"}
            type={"secondary"}
            onClick={() => {
              answersResource.reload(props.inspection.id);
              findingsResource.reload(props.inspection.id);
              availableSeveritiesResource.reload(props.inspection.id);
            }}
          />
          <Button
            type={"primary"}
            icon={faFloppyDisk}
            size={"md"}
            text={saving ? "Opslaan..." : "Antwoorden opslaan"}
            disabled={!dirty || saving}
            onClick={save}
          />
        </div>
      </div>
      {rowsWithDuplicateFindings.length > 0 && (
        <div
          className={
            "mb-4 flex items-start rounded-md border border-yellow-300 bg-yellow-50 px-6 py-3 text-sm text-yellow-800"
          }
        >
          <FontAwesomeIcon icon={faClone} className={"mr-3 mt-0.5"} />
          <div>
            <div className={"font-semibold"}>Dubbele bevindingen gevonden</div>
            <div>
              {rowsWithDuplicateFindings.length === 1
                ? "Bij 1 vraag staat meer dan één bevinding."
                : `Bij ${rowsWithDuplicateFindings.length} vragen staat meer dan één bevinding.`}{" "}
              Verwijder per vraag de dubbele bevinding(en) en houd de juiste over:{" "}
              {rowsWithDuplicateFindings.map((row, index) => (
                <span key={row.id}>
                  {index > 0 && ", "}
                  <button
                    type={"button"}
                    className={"underline font-medium hover:text-yellow-900 cursor-pointer"}
                    onClick={() =>
                      document
                        .getElementById(questionRowElementId(row.question.id))
                        ?.scrollIntoView({ behavior: "smooth", block: "center" })
                    }
                  >
                    {row.question.number
                      ? `vraag ${row.question.number}`
                      : row.question.shortDescription || localizeText(row.question.header) || "vraag"}
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className={"space-y-4"}>
        {groupedRows.map((group, index) => (
          <QuestionGroupCard
            key={index}
            title={group.title}
            description={group.description}
            rows={group.rows}
            availableSeverities={availableSeveritiesResource.resource ?? []}
            onAnswerChanged={setAnswer}
            onStageFinding={stageFindingChange}
          />
        ))}
        {rows.length === 0 && <div className={"text-center py-10 text-slate-500"}>Geen vragen</div>}
      </div>
    </Loading>
  );
};

const QuestionGroupCard: FC<{
  title: string;
  description: string;
  rows: QuestionRow[];
  availableSeverities: AvailableSeverities;
  onAnswerChanged: (questionId: string, value: QuestionAnswerValue) => void;
  onStageFinding: (change: FindingChange) => void;
}> = (props) => {
  const descriptionNode = useMemo(() => renderQuestionText(props.description), [props.description]);
  return (
    <Card title={props.title}>
      {props.description && (
        <div className={"text-sm text-slate-600 -mt-2 mb-4 whitespace-pre-line"}>{descriptionNode}</div>
      )}
      <div className={"-mx-6 divide-y divide-slate-100"}>
        {props.rows.map((row) => (
          <QuestionDataRow
            key={row.id}
            row={row}
            availableSeverities={props.availableSeverities}
            onAnswerChanged={(value) => props.onAnswerChanged(row.question.id, value)}
            onStageFinding={props.onStageFinding}
          />
        ))}
      </div>
    </Card>
  );
};

const QuestionDataRow: FC<{
  row: QuestionRow;
  availableSeverities: AvailableSeverities;
  onAnswerChanged: (value: QuestionAnswerValue) => void;
  onStageFinding: (change: FindingChange) => void;
}> = (props) => {
  const question = props.row.question;
  const description = localizeText(question.description);
  const descriptionNode = useMemo(() => renderQuestionText(description), [description]);
  const headerText = question.shortDescription || localizeText(question.header);
  const hasHeader = Boolean(question.number || headerText);
  return (
    <div id={questionRowElementId(question.id)} className={"px-6 py-4 scroll-mt-24"}>
      <div className={"grid grid-cols-1 lg:grid-cols-10 gap-4 items-start"}>
        <div className={`lg:col-span-6 ${hasHeader ? "" : "self-center"}`}>
          {hasHeader && (
            <div className={"font-medium flex items-baseline"}>
              {question.number && (
                <span
                  className={
                    "inline-flex items-center justify-center min-w-7 h-6 px-1.5 rounded-full bg-brand-100 text-brand-900 text-xs font-bold mr-3 shrink-0"
                  }
                >
                  {question.number}
                </span>
              )}
              <span>{headerText}</span>
            </div>
          )}
          {description && (
            <div className={`text-sm text-slate-600 whitespace-pre-line ${hasHeader ? "mt-1.5 ml-10" : ""}`}>
              {descriptionNode}
            </div>
          )}
        </div>
        <div className={"lg:col-span-4"}>
          <QuestionAnswerCell question={question} value={props.row.answer} onChange={props.onAnswerChanged} />
        </div>
      </div>
      <QuestionFindingCell
        question={question}
        answer={props.row.answer}
        findings={props.row.findings}
        availableSeverities={props.availableSeverities}
        onStage={props.onStageFinding}
      />
    </div>
  );
};
